'use client';

import { useCallback, useEffect, useRef } from 'react';
import {
  createGoogleMap,
  createDefaultPin,
  addAdvancedMarkers,
} from '@/lib/services/google-maps/maps.client';
import type { GoogleMapInstance, GoogleMapsWindow, GoogleMarkerInstance, MapCenter } from './types';
import type { FlatLocality } from '@/lib/services/location-services/types';
import { checkLocationPageExists } from '@/lib/actions';
import { DEFAULT_MAP_CENTER, DEFAULT_MAP_ZOOM } from './constants';
import { ServiceLocationsMapProps } from './types';
import { triggerScheduleModal } from './ServiceLocationsSearchForm';

export default function ServiceLocationsMap({
  apiKey,
  center = DEFAULT_MAP_CENTER,
  zoom = DEFAULT_MAP_ZOOM,
  markerLink,
  showMarker = true,
  locations = [],
}: ServiceLocationsMapProps) {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<GoogleMapInstance | null>(null);
  const markersRef = useRef<GoogleMarkerInstance[]>([]);
  const markerModeRef = useRef<'multi' | 'single'>('multi');

  const clearMarkers = useCallback(() => {
    markersRef.current.forEach((marker) => {
      marker.map = null;
    });
    markersRef.current = [];
  }, []);

  const fitMapToLocations = useCallback((map: GoogleMapInstance, locs: FlatLocality[]) => {
    const validLocations = locs.filter(
      (loc) => typeof loc.lat === 'number' && typeof loc.lng === 'number',
    );

    if (!validLocations.length) {
      return;
    }

    const googleMaps = (window as GoogleMapsWindow).google?.maps;
    if (!googleMaps) {
      return;
    }

    const bounds = new googleMaps.LatLngBounds();

    validLocations.forEach((loc) => {
      bounds.extend({ lat: loc.lat, lng: loc.lng });
    });

    map.fitBounds(bounds, 60);

    googleMaps.event.addListenerOnce(map, 'bounds_changed', () => {
      const currentZoom = map.getZoom();

      if (currentZoom && currentZoom > 9) {
        map.setZoom(9);
      }
    });
  }, []);

  const placeMarker = useCallback(
    async (
      map: GoogleMapInstance,
      key: string,
      position: MapCenter,
      locs: FlatLocality[] = [],
      singleMarkerLink?: string | null,
    ) => {
      try {
        const isMulti = locs.length > 0;
        markerModeRef.current = isMulti ? 'multi' : 'single';

        let markersData;

        if (isMulti) {
          markersData = await Promise.all(
            locs.map(async (loc, i) => {
              const uniquePin = await createDefaultPin(key, {
                background: 'rgb(var(--brand-accent))',
                borderColor: '#FFFFFF',
                glyphColor: '#FFFFFF',
                scale: 0.6,
              });
              uniquePin.style.cursor = 'pointer';

              return {
                id: `service-location-pin-${i}`,
                position: { lat: loc.lat, lng: loc.lng },
                title: loc.locality,
                content: uniquePin,
                zIndex: 10,
                onClick: async () => {
                  try {
                    const exists = await checkLocationPageExists(loc.link);
                    if (exists) {
                      window.location.href = `/locations/${loc.link.replace(/^\//, '')}`;
                    } else {
                      triggerScheduleModal();
                    }
                  } catch {
                    triggerScheduleModal();
                  }
                },
              };
            }),
          );
        } else {
          const singlePin = await createDefaultPin(key, {
            background: 'rgb(var(--brand-accent))',
            borderColor: '#FFFFFF',
            glyphColor: '#FFFFFF',
            scale: 2.0,
          });

          if (singleMarkerLink) {
            singlePin.style.cursor = 'pointer';
          }

          markersData = [
            {
              id: 'service-location-pin',
              position,
              title: 'Service Location',
              content: singlePin,
              zIndex: 10,
              onClick: singleMarkerLink
                ? async () => {
                    try {
                      const exists = await checkLocationPageExists(singleMarkerLink);
                      if (exists) {
                        window.location.href = `/locations/${singleMarkerLink.replace(/^\//, '')}`;
                      } else {
                        triggerScheduleModal();
                      }
                    } catch (e) {
                      console.error('[ServiceLocationsMap] validation failed:', e);
                      triggerScheduleModal();
                    }
                  }
                : undefined,
            },
          ];
        }

        const added = await addAdvancedMarkers(key, map, markersData);
        markersRef.current = added;
      } catch (err) {
        console.error('[ServiceLocationsMap] Failed to place marker:', err);
      }
    },
    [],
  );

  useEffect(() => {
    const resolvedApiKey = apiKey;
    const container = mapContainerRef.current;

    if (typeof resolvedApiKey !== 'string' || !container) {
      return;
    }

    const safeApiKey: string = resolvedApiKey;
    const safeContainer: HTMLDivElement = container;

    let cancelled = false;

    async function initMap() {
      try {
        const map = await createGoogleMap({
          apiKey: safeApiKey,
          container: safeContainer,
          mapId: 'service-locations-map',
          center,
          zoom,
          disableDefaultUI: false,
          gestureHandling: 'cooperative',
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          panControl: false,
          rotateControl: false,
          zoomControl: true,
          cameraControl: false,
        });

        if (cancelled) return;

        mapInstanceRef.current = map;

        map.addListener('zoom_changed', () => {
          if (markerModeRef.current !== 'multi') return;

          const currentZoom = map.getZoom() || 7;
          const targetScale = Math.max(0.3, Math.min(1.0, 0.3 + (currentZoom - 7) * 0.1));
          const cssScale = targetScale / 0.6;

          markersRef.current.forEach((marker) => {
            if (marker.content) {
              const el = marker.content as HTMLElement;
              el.style.transform = `scale(${cssScale})`;
              el.style.transformOrigin = 'bottom center';
            }
          });
        });

        // Important: do the initial marker render here too.
        clearMarkers();

        if (showMarker) {
          await placeMarker(map, safeApiKey, center, locations, markerLink);

          if (cancelled) return;

          if (locations.length > 0) {
            fitMapToLocations(map, locations);
          } else {
            map.panTo(center);
            map.setZoom(zoom);
          }
        } else {
          map.panTo(center);
          map.setZoom(zoom);
        }
      } catch (err) {
        console.error('[ServiceLocationsMap] Failed to initialize Google Map:', err);
      }
    }

    initMap();

    return () => {
      cancelled = true;
      clearMarkers();
    };
  }, [
    apiKey,
    center,
    zoom,
    showMarker,
    locations,
    markerLink,
    clearMarkers,
    fitMapToLocations,
    placeMarker,
  ]);

  useEffect(() => {
    const map = mapInstanceRef.current;
    const resolvedApiKey = apiKey;

    if (!map || typeof resolvedApiKey !== 'string') {
      return;
    }

    const safeMap: GoogleMapInstance = map;
    const safeApiKey: string = resolvedApiKey;

    let cancelled = false;

    async function updateMap() {
      clearMarkers();

      if (showMarker) {
        await placeMarker(safeMap, safeApiKey, center, locations, markerLink);

        if (cancelled) return;

        if (locations.length > 0) {
          fitMapToLocations(safeMap, locations);
        } else {
          safeMap.panTo(center);
          safeMap.setZoom(zoom);
        }
      } else {
        safeMap.panTo(center);
        safeMap.setZoom(zoom);
      }
    }

    updateMap();

    return () => {
      cancelled = true;
    };
  }, [
    apiKey,
    center,
    zoom,
    showMarker,
    locations,
    markerLink,
    clearMarkers,
    fitMapToLocations,
    placeMarker,
  ]);

  if (!apiKey) {
    return (
      <div className='w-full h-full rounded-2xl bg-zinc-200 flex items-center justify-center'>
        <div className='text-center text-zinc-500'>
          <svg
            className='w-12 h-12 mx-auto mb-3 text-zinc-400'
            fill='none'
            stroke='currentColor'
            viewBox='0 0 24 24'
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth={1.5}
              d='M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7'
            />
          </svg>
          <p className='text-sm font-medium'>Map preview unavailable</p>
          <p className='text-xs mt-1'>Map unavailable</p>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={mapContainerRef}
      className='w-full h-full rounded-2xl'
    />
  );
}
